import moment from 'moment-timezone';
import {
  AvailableSchedulePeriod,
  mergeContiguous,
} from '@techspert-io/schedule-calendar';

const QUARTER_HOUR_MILLISECONDS = 15 * 60 * 1000;

/** The time at the next quarter hour, or now when it's on one */
const nextQuarterHour = () =>
  moment(
    Math.ceil(moment().valueOf() / QUARTER_HOUR_MILLISECONDS) *
      QUARTER_HOUR_MILLISECONDS
  );

/**
 * Leaves out the time before the next quarter hour, trimming the start of
 * periods in progress, then the periods that aren't part of a continuous
 * block long enough for a call. Periods that touch, such as where the
 * preference changes, count as one block, though they're returned separately
 * and in their given order.
 */
export const filterBookablePeriods = <T extends AvailableSchedulePeriod>(
  periods: T[],
  callDuration: number
) => {
  const earliest = nextQuarterHour();

  const upcoming = periods.flatMap((period) => {
    if (moment(period.end).isSameOrBefore(earliest)) {
      return [];
    }

    return [
      moment(period.start).isSameOrAfter(earliest)
        ? period
        : { ...period, start: earliest.toISOString() },
    ];
  });

  const longEnoughBlocks = mergeContiguous(upcoming).filter(({ start, end }) =>
    moment(start).add(callDuration, 'minutes').isSameOrBefore(end)
  );

  return upcoming.filter((period) =>
    longEnoughBlocks.some(
      ({ start, end }) =>
        moment(start).isSameOrBefore(period.start) &&
        moment(end).isSameOrAfter(period.end)
    )
  );
};
